/**
 * unbridged-cross-domain lint rule — flags references that cross domain
 * boundaries without a documented bridge tag. Cross-domain coupling that
 * isn't intentional makes the model harder to refactor.
 */
import type { AtlasManifest } from '../types.js';

export interface BridgeFinding {
  ref: string;
  message: string;
}

/**
 * A bridge is documented via a tag of the form `bridge:from-to` on the
 * source entity (e.g. `bridge:investments-lending`).
 */
function hasBridgeTag(entityTags: string[], fromDomain: string, toDomain: string): boolean {
  return entityTags.some(
    (t) => t === `bridge:${fromDomain}-${toDomain}` || t === `bridge:${toDomain}-${fromDomain}`,
  );
}

export function unbridgedCrossDomain(manifest: AtlasManifest): BridgeFinding[] {
  const byId = new Map(manifest.entities.map((e) => [e.id, e]));
  const findings: BridgeFinding[] = [];
  const seen = new Set<string>();

  for (const ref of manifest.references) {
    const from = byId.get(ref.from);
    const to = byId.get(ref.to);
    if (!from || !to) continue;
    if (from.domain === to.domain) continue;
    if (hasBridgeTag(from.tags, from.domain, to.domain)) continue;
    const key = `${from.id}|${to.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    findings.push({
      ref: `${from.id} → ${to.id}`,
      message: `${from.id} (${from.domain}) → ${to.id} (${to.domain}) crosses domains without a bridge:* tag`,
    });
  }
  return findings;
}
